// ============================================================
// GLYPH PICKER SCENE
// Full 120-glyph selection panel (12x10 grid).
// Launched from the dictionary screen; clicking any glyph
// returns to the dictionary with that glyph selected.
// ============================================================

import Phaser from 'phaser';
import {
    GLYPH_COLS,
    GLYPH_ROWS,
    GLYPH_SIZE,
    GLYPH_BUFFER,
    SPECIAL_GLYPHS,
    getGlyphIndex,
    getGlyphCount,
    getGlyphColor,
} from '../toki/TokiPonaData';

const PADDING = 4;
const BUTTON_SIZE = 16;

export class GlyphPickerScene extends Phaser.Scene {
    private onSelect: ((index: number) => void) | null = null;
    private onClose: (() => void) | null = null;
    private mobile: boolean = false;

    constructor() {
        super({ key: 'GlyphPickerScene' });
    }

    init(data: {
        mobile?: boolean;
        onSelect?: (index: number) => void;
        onClose?: () => void;
    }): void {
        this.onSelect = data.onSelect || null;
        this.onClose = data.onClose || null;
        this.mobile = data.mobile ?? false;
    }

    create(): void {
        const { width, height } = this.cameras.main;

        // Semi-transparent overlay
        const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.9);
        overlay.setOrigin(0, 0).setDepth(0);

        // Escape button (top-right)
        const alaIndex = getGlyphIndex(SPECIAL_GLYPHS.ESCAPE);
        const escBtn = this.add.image(
            width - PADDING - BUTTON_SIZE, PADDING,
            'toki_glyphs', alaIndex
        );
        escBtn.setOrigin(0, 0);
        escBtn.setDisplaySize(BUTTON_SIZE, BUTTON_SIZE);
        escBtn.setDepth(10);
        escBtn.setTint(getGlyphColor(alaIndex));
        escBtn.setInteractive({ useHandCursor: true });
        escBtn.on('pointerdown', () => this.closeScene());

        // ESC key
        const escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        escKey.on('down', () => this.closeScene());

        // On mobile, spread glyphs across full viewport for easier tapping
        const hSpacing = this.mobile
            ? Math.floor((width - 2 * PADDING) / GLYPH_COLS)
            : GLYPH_SIZE + GLYPH_BUFFER;
        const vSpacing = this.mobile
            ? Math.floor((height - 2 * PADDING) / GLYPH_ROWS)
            : GLYPH_SIZE + GLYPH_BUFFER;
        const gridTotalWidth = (GLYPH_COLS - 1) * hSpacing + GLYPH_SIZE;
        const gridTotalHeight = (GLYPH_ROWS - 1) * vSpacing + GLYPH_SIZE;
        const gridStartX = Math.floor((width - gridTotalWidth) / 2);
        const gridStartY = Math.floor((height - gridTotalHeight) / 2);

        // Wider hitbox on mobile to cover the full cell
        const hitWidth = this.mobile ? hSpacing : 10;
        const hitHeight = this.mobile ? vSpacing : 10;

        const glyphCount = getGlyphCount();
        for (let i = 0; i < glyphCount; i++) {
            const col = i % GLYPH_COLS;
            const row = Math.floor(i / GLYPH_COLS);
            const x = gridStartX + col * hSpacing;
            const y = gridStartY + row * vSpacing;

            const glyph = this.add.image(x, y, 'toki_glyphs', i);
            glyph.setOrigin(0, 0);
            glyph.setDepth(5);
            glyph.setInteractive(
                new Phaser.Geom.Rectangle(-1, -1, hitWidth, hitHeight),
                Phaser.Geom.Rectangle.Contains,
            );
            glyph.input!.cursor = 'pointer';

            const baseColor = getGlyphColor(i);
            glyph.setTint(baseColor);

            glyph.on('pointerdown', () => this.pickGlyph(i));
            glyph.on('pointerover', () => glyph.setTint(0xffffff));
            glyph.on('pointerout', () => glyph.setTint(baseColor));
        }
    }

    private pickGlyph(index: number): void {
        this.onSelect?.(index);
        this.scene.stop();
    }

    private closeScene(): void {
        this.onClose?.();
        this.scene.stop();
    }
}
